import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import '../styles/Header.css';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isAboutDropdownOpen, setIsAboutDropdownOpen] = useState(false);
  const [isProjectsDropdownOpen, setIsProjectsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const pathname = location.pathname;

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Close mobile menu when changing routes - với delay để cho phép navigation
  useEffect(() => {
    // Delay một chút để navigation hoàn thành trước khi đóng menu
    const timer = setTimeout(() => {
      setIsMobileMenuOpen(false);
      setIsAboutDropdownOpen(false);
      setIsProjectsDropdownOpen(false);
    }, 100); // 100ms delay

    return () => clearTimeout(timer);
  }, [pathname]);

  // Handle dropdown hover with delay
  const handleAboutMouseEnter = () => {
    setIsAboutDropdownOpen(true);
  };

  const handleAboutMouseLeave = () => {
    setTimeout(() => {
      setIsAboutDropdownOpen(false);
    }, 50);
  };

  const handleProjectsMouseEnter = () => {
    setIsProjectsDropdownOpen(true);
  };

  const handleProjectsMouseLeave = () => {
    setTimeout(() => {
      setIsProjectsDropdownOpen(false);
    }, 50);
  };

  // Handle click outside to close dropdowns - cải thiện logic
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Chỉ đóng nếu click bên ngoài header hoàn toàn
      const headerElement = event.target.closest('.header');
      const isDropdownLink = event.target.closest('.mobile-dropdown-link');
      
      // Không đóng nếu click vào dropdown link để cho phép navigation
      if (isDropdownLink) {
        return;
      }
      
      // Đóng dropdown desktop nếu click bên ngoài
      if (!event.target.closest('.dropdown')) {
        setIsAboutDropdownOpen(false);
        setIsProjectsDropdownOpen(false);
      }
      
      // Đóng mobile menu nếu click bên ngoài header
      if (!headerElement && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
        setIsAboutDropdownOpen(false);
        setIsProjectsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  // Handle mobile dropdown link click - đảm bảo navigation hoạt động
  const handleDropdownLinkClick = (e) => {
    // Không preventDefault để cho phép navigation
    console.log('Dropdown link clicked:', e.target.href);
    
    // Đóng dropdown sau một delay ngắn để navigation hoàn thành
    setTimeout(() => {
      setIsAboutDropdownOpen(false);
      setIsProjectsDropdownOpen(false);
    }, 150);
  };

  // Simple mobile dropdown toggle functions
  const handleMobileAboutToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('About dropdown clicked');
    setIsAboutDropdownOpen(!isAboutDropdownOpen);
    setIsProjectsDropdownOpen(false);
  };

  const handleMobileProjectsToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('Projects dropdown clicked');
    setIsProjectsDropdownOpen(!isProjectsDropdownOpen);
    setIsAboutDropdownOpen(false);
  };

  // Simple mobile menu toggle
  const handleMobileMenuToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('Mobile menu clicked');
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Check if a link is active
  const isActive = (path) => {
    if (path === '/' && pathname === '/') return true;
    if (path !== '/' && pathname.startsWith(path)) return true;
    return false;
  };

  // Check if dropdown section is active
  const isAboutActive = () => {
    return pathname.startsWith('/about-us');
  };

  const isProjectsActive = () => {
    return pathname.startsWith('/projects');
  };

  return (
    <header className={`header ${isScrolled ? 'scrolled' : 'not-scrolled'}`}>
      <div className="header-container">
        <Link to="/" className="header-logo">
          <div className="header-logo-content">
            <span className="header-logo-span">
              <img 
                src="/images/LOGO2.png" 
                alt="Phúc An Solutions Logo" 
              />
            </span>
          </div>
        </Link>
        
        {/* Desktop Navigation */}
        <nav className="header-nav">
          <Link 
            to="/"
            className={`header-nav-link ${isActive('/') ? 'active' : ''}`}
          >
            HOME
          </Link>

          {/* About Us Dropdown */}
          <div 
            className="dropdown"
            onMouseEnter={handleAboutMouseEnter}
            onMouseLeave={handleAboutMouseLeave}
          >
            <button 
              className={`dropdown-button ${isAboutActive() ? 'active' : ''}`}
            >
              ABOUT US
              <svg 
                xmlns="http://www.w3.org/2000/svg"
                className={`dropdown-icon ${isAboutDropdownOpen ? 'open' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            <div 
              className={`dropdown-menu about ${isAboutDropdownOpen ? 'visible' : 'hidden'}`}
              onMouseEnter={handleAboutMouseEnter}
              onMouseLeave={handleAboutMouseLeave}
            >
              <Link 
                to="/about-us/profile"
                className={`dropdown-menu-link ${isActive('/about-us/profile') ? 'active' : ''}`}
              >
                PROFILE
              </Link>
              <Link 
                to="/about-us/history"
                className={`dropdown-menu-link ${isActive('/about-us/history') ? 'active' : ''}`}
              >
                HISTORY
              </Link>
              <Link 
                to="/about-us/philosophy"
                className={`dropdown-menu-link ${isActive('/about-us/philosophy') ? 'active' : ''}`}
              >
                PHILOSOPHY
              </Link>
            </div>
          </div>

          {/* Projects Dropdown */}
          <div 
            className="dropdown"
            onMouseEnter={handleProjectsMouseEnter}
            onMouseLeave={handleProjectsMouseLeave}
          >
            <button 
              className={`dropdown-button ${isProjectsActive() ? 'active' : ''}`}
            >
              PROJECTS
              <svg 
                xmlns="http://www.w3.org/2000/svg"
                className={`dropdown-icon ${isProjectsDropdownOpen ? 'open' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            <div 
              className={`dropdown-menu projects ${isProjectsDropdownOpen ? 'visible' : 'hidden'}`}
              onMouseEnter={handleProjectsMouseEnter}
              onMouseLeave={handleProjectsMouseLeave}
            >
              <Link 
                to="/projects/iotcctv"
                className={`dropdown-menu-link ${isActive('/projects/iotcctv') ? 'active' : ''}`}
              >
                IOT - CCTV
              </Link>
              <Link 
                to="/projects/solar"
                className={`dropdown-menu-link ${isActive('/projects/solar') ? 'active' : ''}`}
              >
                SOLAR ENERGY
              </Link>
              <Link 
                to="/projects/architecturalconstruction"
                className={`dropdown-menu-link ${isActive('/projects/architecturalconstruction') ? 'active' : ''}`}
              >
                ARCHITECTURE
              </Link>
              <Link 
                to="/projects/landscapegarden"
                className={`dropdown-menu-link ${isActive('/projects/landscapegarden') ? 'active' : ''}`}
              >
                LANDSCAPE
              </Link>
            </div>
          </div>
          
          <Link 
            to="/news"
            className={`header-nav-link ${isActive('/news') ? 'active' : ''}`}
          >
            NEWS
          </Link>
          
          <Link 
            to="/contact"
            className={`header-nav-link ${isActive('/contact') ? 'active' : ''}`}
          >
            CONTACT
          </Link>
        </nav>
        
        {/* Mobile Menu Button */}
        <button 
          className="mobile-menu-button"
          onClick={handleMobileMenuToggle}
          onTouchStart={(e) => e.stopPropagation()}
          aria-label="Toggle mobile menu"
        >
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            className="mobile-menu-icon" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            {isMobileMenuOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>
      </div>
      
      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="mobile-menu">
          <div className="mobile-menu-content">
            <div className="mobile-menu-item">
              <Link 
                to="/"
                className={`mobile-menu-link ${isActive('/') ? 'active' : ''}`}
              >
                HOME
              </Link>
            </div>
            
            <div className="mobile-menu-item">
              <button 
                onClick={handleMobileAboutToggle}
                onTouchStart={(e) => e.stopPropagation()}
                className={`mobile-dropdown-button ${isAboutActive() ? 'active' : ''}`}
                aria-label="Toggle About Us menu"
                aria-expanded={isAboutDropdownOpen}
              >
                <span>ABOUT US</span>
                <svg 
                  xmlns="http://www.w3.org/2000/svg"
                  className={`mobile-dropdown-icon ${isAboutDropdownOpen ? 'open' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {isAboutDropdownOpen && (
                <div className="mobile-dropdown-content">
                  <div className="mobile-menu-item">
                    <Link 
                      to="/about-us/profile"
                      className={`mobile-dropdown-link ${isActive('/about-us/profile') ? 'active' : ''}`}
                      onClick={handleDropdownLinkClick}
                    >
                      PROFILE
                    </Link>
                  </div>
                  <div className="mobile-menu-item">
                    <Link 
                      to="/about-us/history"
                      className={`mobile-dropdown-link ${isActive('/about-us/history') ? 'active' : ''}`}
                      onClick={handleDropdownLinkClick}
                    >
                      HISTORY
                    </Link>
                  </div>
                  <div className="mobile-menu-item">
                    <Link 
                      to="/about-us/philosophy"
                      className={`mobile-dropdown-link ${isActive('/about-us/philosophy') ? 'active' : ''}`}
                      onClick={handleDropdownLinkClick}
                    >
                      PHILOSOPHY
                    </Link>
                  </div>
                </div>
              )}
            </div>

            <div className="mobile-menu-item">
              <button 
                onClick={handleMobileProjectsToggle}
                onTouchStart={(e) => e.stopPropagation()}
                className={`mobile-dropdown-button ${isProjectsActive() ? 'active' : ''}`}
                aria-label="Toggle Projects menu"
                aria-expanded={isProjectsDropdownOpen}
              >
                <span>PROJECTS</span>
                <svg 
                  xmlns="http://www.w3.org/2000/svg"
                  className={`mobile-dropdown-icon ${isProjectsDropdownOpen ? 'open' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {isProjectsDropdownOpen && (
                <div className="mobile-dropdown-content">
                  <div className="mobile-menu-item">
                    <Link 
                      to="/projects/iotcctv"
                      className={`mobile-dropdown-link ${isActive('/projects/iotcctv') ? 'active' : ''}`}
                      onClick={handleDropdownLinkClick}
                    >
                      IOT - CCTV
                    </Link>
                  </div>
                  <div className="mobile-menu-item">
                    <Link 
                      to="/projects/solar"
                      className={`mobile-dropdown-link ${isActive('/projects/solar') ? 'active' : ''}`}
                      onClick={handleDropdownLinkClick}
                    >
                      SOLAR ENERGY
                    </Link>
                  </div>
                  <div className="mobile-menu-item">
                    <Link 
                      to="/projects/architecturalconstruction"
                      className={`mobile-dropdown-link ${isActive('/projects/architecturalconstruction') ? 'active' : ''}`}
                      onClick={handleDropdownLinkClick}
                    >
                      ARCHITECTURE
                    </Link>
                  </div>
                  <div className="mobile-menu-item">
                    <Link 
                      to="/projects/landscapegarden"
                      className={`mobile-dropdown-link ${isActive('/projects/landscapegarden') ? 'active' : ''}`}
                      onClick={handleDropdownLinkClick}
                    >
                      LANDSCAPE
                    </Link>
                  </div>
                </div>
              )}
            </div>
            
            <div className="mobile-menu-item">
              <Link 
                to="/news"
                className={`mobile-menu-link ${isActive('/news') ? 'active' : ''}`}
              >
                NEWS
              </Link>
            </div>
            
            <div className="mobile-menu-item">
              <Link 
                to="/contact"
                className={`mobile-menu-link ${isActive('/contact') ? 'active' : ''}`}
              >
                CONTACT
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header; 